import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  FaCarAlt,
  FaRoute,
  FaCalendarAlt,
  FaUserCheck,
  FaMapMarkerAlt,
  FaUser,
  FaMoneyBillWave,
  FaChartLine,
  FaWallet,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import DriverLayout from "../../layouts/DriverLayout";
import { isObjectId } from "../../utils/objectIdUtils";
import {
  getDriverProfile,
  getBookingHistory,
  updateDriverLocation,
  reset,
} from "../../features/driver/driverSlice";
import {
  getDriverWallet,
  reset as resetWallet,
} from "../../features/wallet/walletSlice";
import socketService from "../../utils/socketService";
import WalletCard from "../../components/Driver/WalletCard";
import { isDriverAuthenticated } from "../../utils/driverAuthUtils";

import { clearDriverAuth } from "../../features/driver/driverAuthSlice";



const DriverDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { driver, bookingHistory, loading, error, success, message } =
    useSelector((state) => state.driver);
  const lastToastRef = useRef({ msg: null, at: 0 });
  const { wallet, isError: walletError, message: walletMessage } = useSelector((state) => state.wallet);
  const [locationWatcher, setLocationWatcher] = useState(null);

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [historyRequested, setHistoryRequested] = useState(false);

  // Security check - verify authentication on dashboard load
  useEffect(() => {
    if (!isDriverAuthenticated({ driver: driverAuth })) {
      dispatch(clearDriverAuth());
      navigate('/driver/login', { replace: true });
      return;
    }
  }, [driverAuth, dispatch, navigate]);


  // Initialize socket connection for real-time updates - separated into connect and event listener phases
  // Socket initialization - only runs once when component mounts
  useEffect(() => {
    // Connect to socket server - this is a singleton now and will only connect once
    socketService.connect();

    // We'll handle the event listeners in a separate useEffect
    // No cleanup here - we want to keep the socket alive for the entire app session
  }, []); // Empty dependency array means this only runs once

  // State for current assignment moved to the top level of the component

  // Set up event listeners when driver auth changes
  useEffect(() => {
    if (driverAuth) {
      // Get the driver ID - now that we store driver data directly, check both patterns
      const driverId =
        driverAuth._id ||
        (driverAuth.driver && driverAuth.driver._id) ||
        driverAuth.id ||
        (driverAuth.driver && driverAuth.driver.id);

      if (driverId) {
        // Only join if we haven't already joined for this driver

        // Join driver room to receive updates
        socketService.joinDriverRoom(driverId);

        // Listen for driver assignments with a specific handler for this component
        const driverAssignmentHandler = (data) => {

          // Verify this assignment is for the current driver
          if (data.driver._id === driverId) {
            setCurrentAssignment(data);

            // Show notification
            toast.success("You have been assigned a new ride!", {
              onClick: () => navigate("/driver/current-ride"),
            });
          }
        };

        // Register the event handler
        socketService.onDriverAssigned(driverAssignmentHandler);
      }
    }

    // No socket disconnection here - only remove event listeners when component unmounts
  }, [driverAuth, navigate]);

  useEffect(() => {
    // First check if we already have driver profile data
    if (driver && driver._id) {
      // Profile already loaded
      setLoadingProfile(false);

      // Load booking history when driver profile is loaded
      if (!bookingHistory) {
        dispatch(getBookingHistory(driver._id));
      }
      return;
    }

    // Check if we have driver auth and it contains the proper driver object
    if (driverAuth) {
      // Try to get the driver ID from various possible locations in the auth object
      const driverId =
        // Check direct ID on driver object
        driverAuth._id ||
        // Check nested driver object ID
        (driverAuth.driver && driverAuth.driver._id) ||
        // Check for ID property instead of _id
        driverAuth.id ||
        // Check nested driver object with id property
        (driverAuth.driver && driverAuth.driver.id);

      if (driverId) {
        // Validate driver id format before calling API
        if (!isObjectId(String(driverId))) {
          toast.error("Invalid driver session. Please log in again.");
          dispatch(clearDriverAuth());
          navigate('/driver/login', { replace: true });
          setLoadingProfile(false);
          return;
        }

        // Load the driver profile silently (no logs)
        dispatch(getDriverProfile(driverId));
        // Load driver wallet with error handling and periodic refresh
        const loadWallet = () => {
          dispatch(getDriverWallet(driverId)).catch((error) => {
            console.error("Failed to load driver wallet:", error);
          });
        };

        loadWallet();

        // Set up periodic wallet refresh every 30 seconds
        const walletRefreshInterval = setInterval(loadWallet, 30000);

        // Store interval reference for cleanup
        setLocationWatcher(prev => ({ ...prev, walletInterval: walletRefreshInterval }));
      } else {
        toast.error(
          "Unable to load driver profile. Please try logging in again.",
        );
        setLoadingProfile(false);
      }
    } else {
      setLoadingProfile(false);
    }

    // Clean up on unmount - only remove event listeners, don't disconnect the socket
    return () => {
      // Socket disconnection is now handled at the App level
      // No need to disconnect socket here
      dispatch(reset());

      // Clean up intervals if they exist
      if (locationWatcher) {
        if (typeof locationWatcher === 'number') {
          navigator.geolocation.clearWatch(locationWatcher);
        } else if (locationWatcher.walletInterval) {
          clearInterval(locationWatcher.walletInterval);
        }
        setLocationWatcher(null);
      }
    };
  }, [dispatch, driverAuth, driver]);

  useEffect(() => {
    if (error) {
      // Suppress repeated identical toasts
      const now = Date.now();
      const sameAsLast = lastToastRef.current.msg === error && (now - lastToastRef.current.at) < 4000;

      // If error is 'Driver not found', treat it as non-fatal: keep session alive and retry fetching profile
      const isDriverNotFound = typeof error === 'string' && /Driver not found/i.test(error);
      const isAuthError = typeof error === 'string' && /MISSING_AUTH_TOKEN|INVALID_TOKEN|TOKEN_EXPIRED|ACCOUNT_NOT_FOUND/i.test(error);

      if (isDriverNotFound) {
        // Log for debugging and avoid showing a frightening toast to the driver
        console.warn('Non-fatal: Driver not found while fetching profile. Will retry silently.');

        // Retry fetching driver profile once after a short delay (10s)
        setTimeout(() => {
          try {
            const driverId = driverAuth?._id || driverAuth?.driver?._id || driverAuth?.id || driverAuth?.driver?.id;
            if (driverId) dispatch(getDriverProfile(driverId));
          } catch (e) {
            console.warn('Retry for driver profile failed to start:', e.message || e);
          }
        }, 10000);

        // Do not clear auth or navigate away; simply reset state silently
        dispatch(reset());
        setLoadingProfile(false);
        return; // Early return — don't show toast for this specific case
      }

      if (!sameAsLast) {
        // Show error for other (likely important) errors
        toast.error(error);
        lastToastRef.current = { msg: error, at: now };
      }

      // If it's an authentication-related error, clear session and force re-login
      if (isAuthError) {
        dispatch(clearDriverAuth());
        navigate('/driver/login', { replace: true });
      }

      dispatch(reset());
      setLoadingProfile(false);
    }

    if (success) {
      const now = Date.now();
      const sameAsLast = lastToastRef.current.msg === message && (now - lastToastRef.current.at) < 4000;
      if (!sameAsLast) {
        toast.success(message);
        lastToastRef.current = { msg: message, at: now };
      }
      dispatch(reset());
    }

    // Set loading to false when we get the driver profile
    if (driver && driver._id) {
      setLoadingProfile(false);
    }
  }, [error, success, message, dispatch, driver, navigate]);

  useEffect(() => {
    // Start watching location if driver is available
    if (driver && driver._id && driver.isAvailable) {
      startLocationTracking();
    } else if (locationWatcher) {
      // Stop watching if driver becomes unavailable
      navigator.geolocation.clearWatch(locationWatcher);
      setLocationWatcher(null);
    }
  }, [driver]);

  // Use state to track last update time to throttle location updates
  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  const LOCATION_UPDATE_INTERVAL = 60000; // 60 seconds between updates (increased to reduce load)

  // State for current assignment (populated by socket)
  const [currentAssignment, setCurrentAssignment] = useState(null);
  // We'll calculate revenue from actual booking data later

  // Add periodic refresh to keep driver status updated
  useEffect(() => {
    if (driver && driver._id) {
      const refreshInterval = setInterval(() => {
        if (isObjectId(String(driver._id))) {
          dispatch(getDriverProfile(driver._id));
        }
      }, 30000);

      return () => clearInterval(refreshInterval);
    }
  }, [dispatch, driver]);

  // Make sure booking history is fetched if not already loaded and refresh driver profile
  useEffect(() => {
    if (driver && driver._id && !historyRequested) {
      dispatch(getBookingHistory(driver._id));
      setHistoryRequested(true);

      // Also refresh driver profile to ensure current status is accurate
      dispatch(getDriverProfile(driver._id));
    }
  }, [dispatch, driver, historyRequested]);

  // Helper functions for date calculations
  const getStartOfWeek = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
    startOfWeek.setHours(0, 0, 0, 0); // Set to midnight
    return startOfWeek;
  };

  const getStartOfMonth = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
    startOfMonth.setHours(0, 0, 0, 0); // Set to midnight
    return startOfMonth;
  };

  const getBookingsThisWeek = () => {
    if (!bookingHistory || bookingHistory.length === 0) return 0;
    const startOfWeek = getStartOfWeek();

    const allThisWeek = bookingHistory.filter(
      (booking) => new Date(booking.createdAt) >= startOfWeek,
    );
    return allThisWeek.length;
  };

  const getBookingsThisMonth = () => {
    if (!bookingHistory || bookingHistory.length === 0) return 0;
    const startOfMonth = getStartOfMonth();

    const allThisMonth = bookingHistory.filter(
      (booking) => new Date(booking.createdAt) >= startOfMonth,
    );
    return allThisMonth.length;
  };

  // Helper function to get consistent total ride count
  const getConsistentTotalRides = () => {
    // Use driver.totalRides from database to match admin panel
    const databaseTotalRides = driver?.totalRides || 0;

    if (bookingHistory && Array.isArray(bookingHistory)) {
      // If there's a significant discrepancy, show both values
      if (Math.abs(databaseTotalRides - bookingHistory.length) > 5) {
        // Optional: handle discrepancy
      }
    }

    // Return database value to match admin panel
    return databaseTotalRides;
  };

  // Only allow one active location tracking instance
  const startLocationTracking = () => {
    // If we already have an active interval, don't start another one
    if (locationWatcher) {
      return;
    }

    if (navigator.geolocation) {
      try {
        // Set up a simple interval to check location periodically
        const intervalId = setInterval(() => {
          // Only proceed if driver data is available
          if (!driver || !driver._id) return;

          // Check if enough time has passed since last update
          const now = Date.now();
          if (now - lastUpdateTime < LOCATION_UPDATE_INTERVAL) return;

          // Get current position
          navigator.geolocation.getCurrentPosition(
            (position) => {
              // Update driver location
              dispatch(
                updateDriverLocation({
                  driverId: driver._id,
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                }),
              );

              // Update timestamp
              setLastUpdateTime(now);
            },
            // Silent error handling - no logs, no toasts
            () => {},
            {
              enableHighAccuracy: false,
              maximumAge: 30000,
              timeout: 20000,
            },
          );
        }, LOCATION_UPDATE_INTERVAL);

        // Store the interval ID
        setLocationWatcher(intervalId);
      } catch (e) {
        // Silent error - just show toast to user
        toast.error("Unable to track location");
      }
    } else {
      toast.error("Geolocation is not supported by this browser");
    }
  };

  return (
    <DriverLayout>
      {loading || loadingProfile ? (
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
          <div className="ml-4 text-gray-700 font-medium">
            Loading driver dashboard...
          </div>
        </div>
      ) : !driver || !driver._id ? (
        <div className="flex flex-col justify-center items-center h-96">
          <div className="text-red-500 text-xl mb-4">
            Unable to load driver profile
          </div>
          <button
            onClick={() => (window.location.href = "/driver/login")}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            Return to Login
          </button>
        </div>
      ) : (!(driver.documentsVerified === true || driver.documentsVerified === "true") && !(driver.isApproved === true || driver.isApproved === "true")) ? (
        <div className="container mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-red-800 mb-4">
              {!(driver.isApproved === true || driver.isApproved === "true")
                ? "Account Not Approved"
                : "Documents Not Verified"}
            </h2>
            <p className="text-red-700 mb-6 text-lg">
              {!(driver.isApproved === true || driver.isApproved === "true")
                ? "Your account is pending admin approval. Please wait for admin review or contact support."
                : "Your documents are not yet verified. Please wait for admin approval or contact support to complete the verification process."}
            </p>
            <div className="bg-white rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-700 mb-4">Required Documents:</h3>
              <ul className="text-left text-gray-600 space-y-2">
                <li>• Driver's License</li>
                <li>• Vehicle Registration</li>
                <li>• Insurance Certificate</li>
                <li>• Identity Proof</li>
              </ul>
            </div>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate("/driver/profile")}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update Profile
              </button>
              <button
                onClick={() => {
                  dispatch(clearDriverAuth());
                  navigate('/driver/login');
                }}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="container mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Driver Dashboard</h1>
          </div>

          {/* Change Password Modal */}


          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4">
              <div className="flex items-center mb-4 md:mb-0">
                <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold">
                  {(driverAuth?.name || driverAuth?.driver?.name)?.charAt(0).toUpperCase()}
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-semibold">
                    {driverAuth?.name || driverAuth?.driver?.name}
                  </h2>
                  <p className="text-gray-600">{driverAuth?.phone || driverAuth?.driver?.phone}</p>
                  <p className="text-gray-600">{driverAuth?.email || driverAuth?.driver?.email}</p>
                </div>
              </div>

              <div className="flex flex-col">
                <span
                  className={`px-4 py-2 rounded-full text-white text-sm font-medium ${
                    driver?.isAvailable ? "bg-green-500" : "bg-red-500"
                  }`}
                >
                  {driver?.isAvailable ? "Available" : "Unavailable"}
                </span>

                {driver?.currentBooking && (
                  <span className="mt-2 px-4 py-2 rounded-full bg-yellow-500 text-white text-sm font-medium">
                    On Trip
                  </span>
                )}
              </div>
            </div>

            {driver && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Driver Management Information - Only show for vendor drivers */}
                {driver.isVendorDriver && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-700 mb-3">
                      Driver Management
                    </h3>
                    <div className="space-y-2">
                      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🏢</span>
                          <span className="font-medium text-blue-700">
                            Vendor Driver
                          </span>
                        </div>

                        <div className="text-sm text-blue-600">
                          <p className="font-medium">Managed by Vendor:</p>
                          <p className="text-blue-800">
                            {driver.vendorId?.fullName || driver.vendorId?.businessName || 'Vendor Name Not Available'}
                          </p>
                          <p className="text-xs text-blue-500 mt-1">
                            Your rides are assigned through your vendor's dashboard
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">
                    Vehicle Information
                  </h3>
                  <p>
                    <span className="text-gray-600">Vehicle Type:</span>{" "}
                    {driver.vehicleType
                      ? typeof driver.vehicleType === "object"
                        ? driver.vehicleType.name
                        : driver.vehicleType
                      : "Not specified"}
                  </p>
                  <p>
                    <span className="text-gray-600">Vehicle Model:</span>{" "}
                    {driver.vehicleModel}
                  </p>
                  <p>
                    <span className="text-gray-600">Vehicle Number:</span>{" "}
                    {driver.vehicleNumber}
                  </p>
                  <p>
                    <span className="text-gray-600">License Number:</span>{" "}
                    {driver.licenseNumber}
                  </p>
                  <p>
                    <span className="text-gray-600">License Expiry:</span>{" "}
                    {new Date(driver.licenseExpiry).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }).replace(/\//g, ' ')}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">
                    Performance
                  </h3>
                  <p>
                    <span className="text-gray-600">Total Rides:</span>{" "}
                    {getConsistentTotalRides()}
                  </p>
                  {bookingHistory && bookingHistory.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1 space-y-1">
                      <p>• Completed: {bookingHistory.filter(b => b.status === 'completed').length}</p>
                      <p>• In Progress: {bookingHistory.filter(b => b.status === 'inprogress').length}</p>
                      <p>• Assigned: {bookingHistory.filter(b => b.status === 'assigned').length}</p>
                      <p>• Cancelled: {bookingHistory.filter(b => b.status === 'cancelled').length}</p>
                      <p>• Other: {bookingHistory.filter(b => !['completed', 'inprogress', 'assigned', 'cancelled'].includes(b.status)).length}</p>
                    </div>
                  )}
                  <p className="mt-2">
                    <span className="text-gray-600">Average Rating:</span>{" "}
                    {driver.ratings
                      ? driver.ratings.toFixed(1) + " / 5.0"
                      : "No ratings yet"}
                  </p>
                  <p>
                    <span className="text-gray-600">Documents Verified:</span>{" "}
                    <span
                      className={
                        driver.documentsVerified === true ||
                        driver.documentsVerified === "true"
                          ? "text-green-500"
                          : "text-red-500"
                      }
                    >
                      {driver.documentsVerified === true ||
                      driver.documentsVerified === "true"
                        ? "Yes"
                        : "No"}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Data Sync Warning */}
          {bookingHistory && bookingHistory.length > 0 && Math.abs((driver?.totalRides || 0) - bookingHistory.length) > 5 && (
            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.487 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-orange-800">
                    Data Sync Notice
                  </h3>
                  <div className="mt-2 text-sm text-orange-700">
                    <p>
                      Profile shows {driver?.totalRides || 0} total rides, but booking history shows {bookingHistory.length} entries.
                      This may indicate a data sync issue.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">Performance Metrics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-md p-6 transition-transform hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500">Total Rides</p>
                    <h3 className="text-2xl font-bold">
                      {getConsistentTotalRides()}
                    </h3>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <FaCarAlt className="text-blue-500 text-xl" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 transition-transform hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500">This Month</p>
                    <h3 className="text-2xl font-bold">
                      {getBookingsThisMonth()}
                    </h3>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <FaRoute className="text-green-500 text-xl" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 transition-transform hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500">This Week</p>
                    <h3 className="text-2xl font-bold">
                      {getBookingsThisWeek()}
                    </h3>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                    <FaCalendarAlt className="text-yellow-500 text-xl" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 transition-transform hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500">Rating</p>
                    <h3 className="text-2xl font-bold">
                      {driver?.ratings && driver.ratings > 0 ? driver.ratings.toFixed(1) : "No ratings yet"}
                    </h3>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <FaUserCheck className="text-purple-500 text-xl" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Section */}
          <div className="mb-6">
            {walletError && walletMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Wallet Loading Error
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{walletMessage}</p>
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={() => driver?._id && dispatch(getDriverWallet(driver._id))}
                        className="bg-red-100 hover:bg-red-200 text-red-800 text-sm px-3 py-1 rounded-md"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <WalletCard
              wallet={wallet}
              driverId={driver?._id}
              onRefresh={() =>
                driver?._id && dispatch(getDriverWallet(driver._id))
              }
            />


          </div>



          {/* Show current assignment if received via socket */}
          {currentAssignment && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-green-500">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-green-600">
                  New Ride Assignment!
                </h2>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  Just Assigned
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">
                    Pickup Location
                  </h3>
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-red-500 mt-1 mr-2" />
                    <p>
                      {currentAssignment.booking.pickupLocation?.name ||
                        "Location details not available"}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-700 mb-2">
                    Drop Location
                  </h3>
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-blue-500 mt-1 mr-2" />
                    <p>
                      {currentAssignment.booking.dropLocation?.name ||
                        "Location details not available"}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Passenger</h3>
                  <div className="flex items-start">
                    <FaUser className="text-gray-500 mt-1 mr-2" />
                    <p>{currentAssignment.booking.user?.name || "Customer"}</p>
                  </div>
                  {currentAssignment.booking.user?.phone && (
                    <p className="text-sm text-gray-500 ml-6">
                      {currentAssignment.booking.user.phone}
                    </p>
                  )}
                </div>

                <div>
                  <h3 className="font-medium text-gray-700 mb-2">
                    Vehicle Type
                  </h3>
                  <div className="flex items-start">
                    <FaCarAlt className="text-gray-500 mt-1 mr-2" />
                    <p>
                      {currentAssignment.booking.cabType?.name || "Standard"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => navigate("/driver/current-ride")}
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  View Details
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
            {bookingHistory && bookingHistory.length > 0 ? (
              <div className="space-y-4">
                {bookingHistory.slice(0, 3).map((booking, index) => {
                  // Get the appropriate icon and style based on booking status
                  let icon, iconClass, borderClass, title, subtitle;
                  if (booking.status === "completed") {
                    icon = <FaRoute className="text-blue-600" />;
                    iconClass = "bg-blue-100";
                    borderClass = "border-blue-500";
                    title = "Trip Completed";
                    subtitle = `${booking.pickupLocation?.name || "Pickup"} to ${booking.dropLocation?.name || "Destination"}`;
                  } else if (booking.status === "cancelled") {
                    icon = <FaUser className="text-red-600" />;
                    iconClass = "bg-red-100";
                    borderClass = "border-red-500";
                    title = "Booking Cancelled";
                    subtitle = `${booking.pickupLocation?.name || "Pickup"} to ${booking.dropLocation?.name || "Destination"}`;
                  } else if (
                    booking.status === "confirmed" ||
                    booking.status === "assigned"
                  ) {
                    icon = <FaUserCheck className="text-green-600" />;
                    iconClass = "bg-green-100";
                    borderClass = "border-green-500";
                    title = "New Booking";
                    subtitle = `${booking.pickupLocation?.name || "Pickup"} to ${booking.dropLocation?.name || "Destination"}`;
                  } else {
                    icon = <FaMoneyBillWave className="text-purple-600" />;
                    iconClass = "bg-purple-100";
                    borderClass = "border-purple-500";
                    title =
                      booking.status.charAt(0).toUpperCase() +
                      booking.status.slice(1);
                    subtitle = `${booking.pickupLocation?.name || "Pickup"} to ${booking.dropLocation?.name || "Destination"}`;
                  }

                  // Format the date
                  const date = new Date(booking.createdAt);
                  const formattedDate = new Intl.DateTimeFormat("en-US", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  }).format(date);

                  return (
                    <div
                      key={index}
                      className={`flex items-start p-3 border-l-4 ${borderClass} bg-gray-50 rounded`}
                    >
                      <div className={`${iconClass} p-2 rounded-full mr-3`}>
                        {icon}
                      </div>
                      <div>
                        <p className="font-medium">{title}</p>
                        <p className="text-sm text-gray-600">{subtitle}</p>
                        <p className="text-xs text-gray-500">{formattedDate}</p>
                        {booking.fare && (
                          <p className="text-sm font-medium text-green-600 mt-1">
                            ���{booking.fare}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500">No recent activities found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </DriverLayout>
  );
};

export default DriverDashboard;
